import {
  AddedBooking,
  BookingChangesSummary,
  DeletedBooking,
  idFlavor,
  N_BookingEdit,
  N_BookingFieldChange,
  N_BookingFieldUpdateChangeView,
  N_BookingUpdatedValues,
  N_CampaignChange,
  N_CampaignEditWithInfoSchemaLog,
  N_ChangedValue,
  N_MaterialInstructions,
  UpdatedBooking,
} from "@specsheet-common/shared-types";
import { notNull, notNullish } from "../typescript";

const noneValue = "none";

const fromChangedValueToFieldChange = (
  changed: N_ChangedValue<string | undefined>,
  fieldName: string
): N_BookingFieldChange => ({
  fieldName,
  valueBefore: changed?.before || noneValue,
  valueAfter: changed?.after || noneValue,
});

const stringifyMaterialInstructions = (instructions: N_MaterialInstructions) =>
  [
    instructions.creativeRotation !== "" &&
    notNullish(instructions.creativeRotation)
      ? `Creative Rotation: ${instructions.creativeRotation}`
      : undefined,
    instructions.messaging !== "" && notNullish(instructions.messaging)
      ? `Messaging: ${instructions.messaging}`
      : undefined,
    instructions.notes !== "" && notNullish(instructions.notes)
      ? `Notes: ${instructions.notes}`
      : undefined,
    instructions.objectives !== "" && notNullish(instructions.objectives)
      ? `Objectives: ${instructions.objectives}`
      : undefined,
    instructions.targeting !== "" && notNullish(instructions.targeting)
      ? `Targeting: ${instructions.targeting}`
      : undefined,
  ]
    .filter(notNullish)
    .join(" ");

const fromMaterialInstructionsChangedToFieldChanges = (
  materialInstructionsChanged: N_ChangedValue<
    N_MaterialInstructions | undefined
  >
): N_BookingFieldChange =>
  fromChangedValueToFieldChange(
    {
      before: notNullish(materialInstructionsChanged?.before)
        ? stringifyMaterialInstructions(materialInstructionsChanged?.before)
        : noneValue,
      after: notNullish(materialInstructionsChanged?.after)
        ? stringifyMaterialInstructions(materialInstructionsChanged?.after)
        : noneValue,
    },
    "Creative"
  );

const fromBookingUpdatedValuesToFieldChanges = (
  values: N_BookingUpdatedValues
): N_BookingFieldChange[] =>
  [
    values.dispatchDateChanged !== undefined
      ? fromChangedValueToFieldChange(
          values.dispatchDateChanged,
          "Dispatch Date"
        )
      : null,
    values.dispatchDetailsChanged !== undefined
      ? fromChangedValueToFieldChange(
          values.dispatchDetailsChanged,
          "Dispatch Details"
        )
      : null,
    values.dispatchToChanged !== undefined
      ? fromChangedValueToFieldChange(values.dispatchToChanged, "Dispatch To")
      : null,
    values.durationChanged !== undefined
      ? fromChangedValueToFieldChange(values.durationChanged, "Duration")
      : null,
    values.endDateChanged !== undefined
      ? fromChangedValueToFieldChange(values.endDateChanged, "End Date")
      : null,
    values.groupChanged !== undefined
      ? fromChangedValueToFieldChange(
          {
            before: values.groupChanged?.before?.name,
            after: values.groupChanged?.after?.name,
          },
          "Group"
        )
      : null,
    values.liveDateChanged !== undefined
      ? fromChangedValueToFieldChange(values.liveDateChanged, "Live Date")
      : null,
    values.notesChanged !== undefined
      ? fromChangedValueToFieldChange(values.notesChanged, "Notes")
      : null,
    values.materialInstructionsChanged !== undefined
      ? fromMaterialInstructionsChangedToFieldChanges(
          values.materialInstructionsChanged
        )
      : null,
  ].filter(notNull);

const getBookingsAdded = (edits: N_BookingEdit[]): AddedBooking[] =>
  edits.reduce<AddedBooking[]>(
    (bookingsAdded, edit) =>
      edit.kind === "bookingAdded" || edit.kind === "bookingDuplicated"
        ? [
            ...bookingsAdded,
            {
              bookingName: edit.placementName ?? "",
              publisherName: edit.publisherName ?? "",
            },
          ]
        : bookingsAdded,
    []
  );

const getBookingsDeleted = (edits: N_BookingEdit[]): DeletedBooking[] =>
  edits.reduce<DeletedBooking[]>(
    (bookingsDeleted, edit) =>
      edit.kind === "bookingDeleted"
        ? [
            ...bookingsDeleted,
            {
              bookingName: edit.placementName ?? "",
              publisherName: edit.publisherName ?? "",
            },
          ]
        : bookingsDeleted,
    []
  );

const getBookingsUpdated = (
  edits: N_BookingEdit[]
): N_BookingFieldUpdateChangeView[] =>
  edits.reduce<N_BookingFieldUpdateChangeView[]>(
    (bookingsUpdated, edit) =>
      edit.kind === "bookingUpdated"
        ? [
            ...bookingsUpdated,
            {
              bookingId: edit.booking.id,
              description: edit.placementName ?? "",
              publisherName: edit.publisherName ?? "",
              changes: fromBookingUpdatedValuesToFieldChanges(
                edit.updated.updates
              ).filter((ch) => ch.valueAfter !== ch.valueBefore),
            },
          ]
        : bookingsUpdated,
    []
  );

const getBookingEdits = (
  changes: N_CampaignEditWithInfoSchemaLog[]
): N_BookingEdit[] =>
  changes.reduce<N_BookingEdit[]>(
    (bookingEdits, change) =>
      change.kind === "bookingEdit"
        ? [...bookingEdits, change.value]
        : bookingEdits,
    []
  );

const collectBookingsUpdatedChanges = (
  bookingsUpdated: N_BookingFieldUpdateChangeView[]
) => [
  ...bookingsUpdated
    .reduce<Map<idFlavor<"Booking">, N_BookingFieldUpdateChangeView>>(
      (map, bu) =>
        new Map([
          ...map.entries(),
          [
            bu.bookingId,
            {
              ...bu,
              changes: [
                ...(map.get(bu.bookingId)?.changes || []),
                ...bu.changes,
              ],
            },
          ],
        ]),
      new Map()
    )
    .values(),
];

const collectBookingUpdatedFieldChanges = (
  bookingsUpdated: N_BookingFieldUpdateChangeView[]
): N_BookingFieldUpdateChangeView[] =>
  bookingsUpdated.map((bu) => ({
    ...bu,
    changes: [
      ...bu.changes
        .reduce<Map<string, N_BookingFieldChange>>(
          (map, change) =>
            new Map([
              ...map.entries(),
              [
                change.fieldName,
                {
                  ...(map.get(change.fieldName) || change),
                  valueAfter: change.valueAfter,
                },
              ],
            ]),
          new Map()
        )
        .values(),
    ],
  }));

const stringifyBookingsUpdated = (
  bookingsUpdated: N_BookingFieldUpdateChangeView[]
): UpdatedBooking[] =>
  bookingsUpdated.map((bu) => ({
    name: bu.description,
    publisherName: bu.publisherName,
    before: bu.changes
      .map((change) => `${change.fieldName}: ${change.valueBefore}`)
      .join(" "),
    after: bu.changes
      .map((change) => `${change.fieldName}: ${change.valueAfter}`)
      .join(" "),
  }));

export const summarizeBookingChanges = (
  changes: N_CampaignChange[]
): BookingChangesSummary => {
  const bookingEdits = getBookingEdits(
    changes.flatMap((change) => change.changes)
  );

  const bookingsAdded = getBookingsAdded(bookingEdits);

  const bookingsDeleted = getBookingsDeleted(bookingEdits);

  const bookingsUpdated = stringifyBookingsUpdated(
    collectBookingUpdatedFieldChanges(
      collectBookingsUpdatedChanges(getBookingsUpdated(bookingEdits))
    )
  );

  return {
    added: bookingsAdded,
    deleted: bookingsDeleted,
    updated: bookingsUpdated,
  };
};

export const forTestsSummarizeBookingChanges = {
  fromChangedValueToFieldChange,
  stringifyMaterialInstructions,
  fromMaterialInstructionsChangedToFieldChanges,
  fromBookingUpdatedValuesToFieldChanges,
  getBookingsAdded,
  getBookingsDeleted,
  getBookingsUpdated,
  getBookingEdits,
  collectBookingsUpdatedChanges,
  collectBookingUpdatedFieldChanges,
  stringifyBookingsUpdated,
  noneValue,
};
